import * as Yup from 'yup';
import { fixGoDate } from '../helpers/fix-go-types';
import { casesOf, OrderItemSchema, totalCost, unitsOf } from './order-item';
import { OrderOptionsSchema } from './order-options';
import { BounceRecordSchema } from './representative';

export enum OrderStatus {
  Draft = 'draft',
  Sending = 'sending',
  Sent = 'sent',
  Canceled = 'canceled',
  Failed = 'failed',
}

const DownloadInfoSchema = Yup.object().shape({
  file_count: Yup.number().required(),
  max_rows: Yup.number().optional().default(0),
  bulk_add_link: Yup.string().optional().default(''),
  bulk_add_label: Yup.string().optional().default(''),
});

// DeliveryFailure mirrors the Go models.DeliveryFailure (an Email plus an
// embedded BounceRecord, flat on the wire). Reuses BounceRecordSchema so
// the bounce fields stay defined in one place.
export const DeliveryFailureSchema = BounceRecordSchema.shape({
  email: Yup.string().default(''),
});

export type DeliveryFailure = Yup.InferType<typeof DeliveryFailureSchema>;

// form validation rules
export const OrderSchema = Yup.object().shape({
  store_id: Yup.string().required(),
  id: Yup.string().required(),
  vendor_id: Yup.string().required(),

  options: OrderOptionsSchema.required(),
  items: Yup.array().of(OrderItemSchema.required()).required(),
  status: Yup.string().oneOf(Object.values(OrderStatus)).required(),
  send_method: Yup.string().optional(),
  download_info: DownloadInfoSchema.optional().nullable().default(null),

  created_at: Yup.date().required(),
  updated_at: Yup.date().required(),
  submitted_at: Yup.date().nullable().transform(fixGoDate),

  ses_message_id: Yup.string().optional().default(''),
  sent_rep_emails: Yup.array().of(Yup.string().required()).default([]),
  delivery_failures: Yup.array().of(DeliveryFailureSchema.required()).default([]),
});

export type Order = Yup.InferType<typeof OrderSchema>;

// form validation rules
const CreateOrderSchema = Yup.object().shape({
  vendor_id: Yup.string().required(),
  options: OrderOptionsSchema.optional(),
  items: Yup.array().of(OrderItemSchema.required()),
});

export type CreateOrder = Yup.InferType<typeof CreateOrderSchema>;

// Helper functions for calculating order totals
export const caseCount = (items: Order['items']) => {
  return items.reduce((sum, item) => sum + casesOf(item), 0);
};

export const unitCount = (items: Order['items']) => {
  return items.reduce((sum, item) => sum + unitsOf(item), 0);
};

export const orderTotalCost = (items: Order['items']) => {
  return items.reduce((sum, item) => sum + totalCost(item), 0);
};

export type OrderDeliveryState = 'clean' | 'partial' | 'failed' | 'unknown';

// bouncedEmails is the lowercased set of addresses that bounced for an
// order. Casing is normalized here because SES reports recipients in the
// (lowercased) form we sent them, while sent_rep_emails are stored as-sent.
//
// delivery_failures is optional on the wire (omitempty) — the OrderSchema
// default only fills it in for fetched orders, but these helpers also run on
// order objects built client-side (optimistic cache updates), so guard the
// absent case rather than assume the field is always present.
function bouncedEmails(order: Order): Set<string> {
  return new Set((order.delivery_failures ?? []).map((f) => f.email.toLowerCase()));
}

// orderDeliveryState classifies an order's email delivery from the
// vendor's perspective:
//   failed  — every rep address we emailed bounced; the vendor almost
//             certainly never received the order.
//   partial — at least one rep bounced, but at least one other rep
//             address was reachable.
//   unknown — the order recorded delivery failures but we have no record
//             of which reps it was emailed to (an email order identified by
//             ses_message_id, sent before sent_rep_emails shipped). Surface
//             a generic warning rather than hide a real problem.
//   clean   — nothing relevant bounced. A bounce on a store cc address
//             (not in sent_rep_emails) does not downgrade the state: the
//             vendor still received the order.
export function orderDeliveryState(order: Order): OrderDeliveryState {
  if ((order.delivery_failures ?? []).length === 0) {
    return 'clean';
  }

  const repEmails = (order.sent_rep_emails ?? []).map((e) => e.toLowerCase());
  if (repEmails.length === 0) {
    // No recorded rep recipients — an email order sent before
    // sent_rep_emails shipped. A non-empty ses_message_id means we
    // emailed the vendor via SES (set only on email sends, never on
    // ftp/download), so warn generically; otherwise delivery doesn't
    // depend on the vendor email, so stay clean.
    return order.ses_message_id ? 'unknown' : 'clean';
  }

  const bounced = bouncedEmails(order);
  const bouncedReps = repEmails.filter((e) => bounced.has(e));

  if (bouncedReps.length === 0) {
    return 'clean';
  }

  return bouncedReps.length >= repEmails.length ? 'failed' : 'partial';
}

// orderDeliveredReps returns the vendor representative addresses that DID
// receive the order: the ones we emailed (sent_rep_emails) minus the ones
// that bounced. Casing is preserved for display; the bounce match is
// case-insensitive. Empty for failed orders (every rep bounced) and for
// legacy orders with no recorded reps (we can't say who received it).
export function orderDeliveredReps(order: Order): string[] {
  const bounced = bouncedEmails(order);

  return (order.sent_rep_emails ?? []).filter((e) => !bounced.has(e.toLowerCase()));
}
